import React, { useState } from 'react';
import { UtensilsCrossed, Coffee, Truck, Sun, Sunrise, Sunset, Clock } from 'lucide-react';

const BRAND    = '#E8401A';
const BRAND_BG = 'rgba(232,64,26,0.08)';
const BORDER   = '#E5E2DC';
const TEXT_1   = '#1A1A1A';
const TEXT_2   = '#5C5A58';

// ── Venue types ───────────────────────────────────────────────────────────────

const VENUE_TYPES = [
  { id: 'kitchen',  label: 'Full Kitchen',         sub: 'Restaurant, pub kitchen, canteen, hotel' },
  { id: 'light',    label: 'Light Food Service',   sub: 'Café, coffee shop, bar with light menu' },
  { id: 'catering', label: 'Catering & Events',    sub: 'Offsite and event catering' },
];

// ── Temperature equipment ─────────────────────────────────────────────────────

const TEMP_TYPES_BY_VENUE = {
  kitchen:  ['fridge', 'freezer', 'hotHold', 'cooking'],
  light:    ['fridge', 'freezer'],
  catering: ['fridge', 'freezer', 'hotHold', 'cooking'],
};

const TEMP_META = {
  fridge:  { label: 'Fridge',              threshold: { max: 5 },   correctiveActions: ['Moved to working fridge',  'Supervisor notified', 'Repair booked', 'Stock discarded'] },
  freezer: { label: 'Freezer',             threshold: { max: -18 }, correctiveActions: ['Moved to working freezer', 'Supervisor notified', 'Repair booked', 'Stock discarded'] },
  hotHold: { label: 'Hot Holding',         threshold: { min: 60 },  correctiveActions: ['Reheated', 'Discarded', 'Supervisor notified'] },
  cooking: { label: 'Cooking / Reheating', threshold: { min: 75 },  correctiveActions: ['Reheated again', 'Discarded', 'Supervisor notified'] },
};

const TEMP_COUNT_DEFAULTS = {
  kitchen:  { fridge: 2, freezer: 1, hotHold: 1, cooking: 1 },
  light:    { fridge: 1, freezer: 1 },
  catering: { fridge: 1, freezer: 1, hotHold: 1, cooking: 1 },
};

const NAME_SUGGESTIONS = {
  fridge:  ['Walk-in Fridge', 'Prep Fridge', 'Display Fridge', 'Back-of-house Fridge'],
  freezer: ['Freezer', 'Chest Freezer', 'Upright Freezer', 'Freezer 4'],
  hotHold: ['Hot Holding', 'Hot Hold 2', 'Hot Hold 3'],
  cooking: ['Cooking / Reheating'],
};

function getDefaultName(type, idx, venueType) {
  const list = NAME_SUGGESTIONS[type] || [];
  return list[idx] ?? `${TEMP_META[type]?.label ?? type} ${idx + 1}`;
}

function initTemps(venueType) {
  const types = TEMP_TYPES_BY_VENUE[venueType] || [];
  const counts = TEMP_COUNT_DEFAULTS[venueType] || {};
  const result = {};
  types.forEach(type => {
    const count = counts[type] || 0;
    result[type] = {
      count,
      names: Array.from({ length: count }, (_, i) => getDefaultName(type, i, venueType)),
    };
  });
  return result;
}

// ── Checklists ────────────────────────────────────────────────────────────────

const CHECKLIST_ENABLED_DEFAULTS = {
  kitchen:  { opening: true, closing: true, cleaning: true, delivery: true,  hygiene: false },
  light:    { opening: true, closing: true, cleaning: true, delivery: false, hygiene: false },
  catering: { opening: true, closing: true, cleaning: true, delivery: true,  hygiene: false },
};

const CHECKLIST_ITEM_DEFAULTS = {
  opening: {
    kitchen:  ['Fridge temps checked', 'Freezer temps checked', 'Equipment operational', 'Surfaces clean', 'Handwashing stations stocked'],
    light:    ['Fridge temps checked', 'Equipment operational', 'Surfaces clean', 'Handwashing stations stocked'],
    catering: ['Fridge temps checked', 'Freezer temps checked', 'Equipment operational', 'Transport vehicle checked', 'Surfaces clean'],
  },
  closing: {
    kitchen:  ['Food covered and stored', 'Equipment off and clean', 'Surfaces sanitised', 'Waste removed'],
    light:    ['Food covered and stored', 'Equipment off', 'Surfaces clean', 'Waste removed'],
    catering: ['Food packed and refrigerated', 'Equipment cleaned', 'Vehicle cleaned', 'Waste removed'],
  },
  delivery: {
    kitchen:  ['Check delivery temperature', 'Inspect packaging condition', 'Check use-by dates', 'Record supplier and time'],
    light:    ['Check delivery temperature', 'Inspect packaging condition', 'Check use-by dates', 'Record supplier'],
    catering: ['Check delivery temperature', 'Inspect packaging', 'Check use-by dates', 'Record supplier', 'Check vehicle temp'],
  },
  hygiene: {
    kitchen:  ['Handwashing completed', 'PPE worn as required', 'No illness declared', 'Hair tied back / hat worn'],
    light:    ['Handwashing completed', 'PPE worn', 'No illness declared'],
    catering: ['Handwashing completed', 'PPE worn', 'No illness declared', 'Hair tied back / hat worn', 'Health declaration signed'],
  },
};

const CLEANING_ITEM_DEFAULTS = [
  { name: 'Wipe down surfaces',         frequency: 'daily'   },
  { name: 'Sanitise prep areas',        frequency: 'daily'   },
  { name: 'Clean equipment',            frequency: 'daily'   },
  { name: 'Sweep and mop floors',       frequency: 'daily'   },
  { name: 'Deep clean fridges',         frequency: 'weekly'  },
  { name: 'Degrease cooking equipment', frequency: 'weekly'  },
  { name: 'Clean exhaust filters',      frequency: 'monthly' },
  { name: 'Check pest control',         frequency: 'monthly' },
];

const CHECKLIST_LABELS = {
  opening:  'Opening Checks',
  closing:  'Closing Checks',
  cleaning: 'Cleaning Schedule',
  delivery: 'Delivery Receiving',
  hygiene:  'Staff Hygiene',
};

function toToggleItems(names) {
  return names.map(name => ({ name, on: true }));
}

function initChecklists(venueType) {
  const enabled = CHECKLIST_ENABLED_DEFAULTS[venueType] || {};
  return {
    opening:  { enabled: enabled.opening  ?? false, items: toToggleItems(CHECKLIST_ITEM_DEFAULTS.opening[venueType]  || CHECKLIST_ITEM_DEFAULTS.opening.kitchen) },
    closing:  { enabled: enabled.closing  ?? false, items: toToggleItems(CHECKLIST_ITEM_DEFAULTS.closing[venueType]  || CHECKLIST_ITEM_DEFAULTS.closing.kitchen) },
    cleaning: { enabled: enabled.cleaning ?? false, items: CLEANING_ITEM_DEFAULTS.map(i => ({ ...i })) },
    delivery: { enabled: enabled.delivery ?? false, items: toToggleItems(CHECKLIST_ITEM_DEFAULTS.delivery[venueType] || CHECKLIST_ITEM_DEFAULTS.delivery.kitchen) },
    hygiene:  { enabled: enabled.hygiene  ?? false, items: toToggleItems(CHECKLIST_ITEM_DEFAULTS.hygiene[venueType]  || CHECKLIST_ITEM_DEFAULTS.hygiene.kitchen) },
  };
}

// ── Section builder ───────────────────────────────────────────────────────────

function buildSections(venueType, temps, checklists) {
  const sections = [];
  let idx = 0;
  const uid = () => `section_${Date.now()}_${(idx++).toString().padStart(3, '0')}`;

  (TEMP_TYPES_BY_VENUE[venueType] || []).forEach(type => {
    const { count, names } = temps[type] || { count: 0, names: [] };
    for (let i = 0; i < count; i++) {
      const meta = TEMP_META[type];
      const name = names[i] || getDefaultName(type, i, venueType);
      sections.push({
        id: uid(),
        title: name,
        type,
        threshold: { ...meta.threshold },
        correctiveActions: [...meta.correctiveActions],
        items: [name],
        ...(type === 'cooking' ? { dynamicItems: true } : {}),
      });
    }
  });

  ['opening', 'closing', 'cleaning', 'delivery', 'hygiene'].forEach(key => {
    const cl = checklists[key];
    if (!cl?.enabled) return;
    if (key === 'cleaning') {
      sections.push({
        id: uid(),
        title: CHECKLIST_LABELS[key],
        type: 'custom',
        threshold: {},
        correctiveActions: [],
        items: cl.items.map(i => i.name),
        itemFrequencies: Object.fromEntries(cl.items.map(i => [i.name, i.frequency])),
      });
    } else {
      sections.push({
        id: uid(),
        title: CHECKLIST_LABELS[key],
        type: 'custom',
        threshold: {},
        correctiveActions: [],
        items: cl.items.filter(i => i.on !== false).map(i => i.name),
      });
    }
  });

  return sections;
}

// ── Progress dots ─────────────────────────────────────────────────────────────

function ProgressDots({ step, total }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '18px 0 4px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: 5, width: i + 1 === step ? 18 : 5,
          borderRadius: 999,
          background: i + 1 <= step ? BRAND : BORDER,
          transition: 'all 0.25s',
        }} />
      ))}
    </div>
  );
}

// ── Shared button styles ──────────────────────────────────────────────────────

const btnBack = {
  width: 50, height: 50, borderRadius: 14, border: `1.5px solid ${BORDER}`,
  background: '#fff', cursor: 'pointer', fontSize: 18, color: TEXT_2,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};

const btnPrimary = {
  flex: 1, height: 50, borderRadius: 14, border: 'none',
  background: BRAND, color: '#fff',
  fontSize: 15, fontWeight: 600, cursor: 'pointer',
};

// ── Step 1: Venue type ────────────────────────────────────────────────────────

const VENUE_ICONS = {
  kitchen:  <UtensilsCrossed size={22} />,
  light:    <Coffee size={22} />,
  catering: <Truck size={22} />,
};

const Checkmark = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
    <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function VenueCard({ v, selected, onSelect }) {
  const isOn = selected === v.id;
  const icon = VENUE_ICONS[v.id];
  return (
    <button onClick={() => onSelect(v.id)} style={{
      padding: '16px', borderRadius: 14, textAlign: 'left',
      border: `2px solid ${isOn ? BRAND : BORDER}`,
      background: isOn ? BRAND_BG : '#fff',
      cursor: 'pointer', transition: 'all 0.15s',
      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
      position: 'relative',
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 14, flexShrink: 0,
        background: isOn ? BRAND : '#F5F2ED',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isOn ? '#fff' : TEXT_2, transition: 'background 0.15s, color 0.15s',
      }}>
        {React.cloneElement(icon, { size: 26 })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: isOn ? BRAND : TEXT_1 }}>{v.label}</div>
        <div style={{ fontSize: 12, color: TEXT_2, marginTop: 3, lineHeight: 1.4 }}>{v.sub}</div>
      </div>
      {isOn && (
        <div style={{ position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%', background: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Checkmark />
        </div>
      )}
    </button>
  );
}

function StepVenueType({ selected, onSelect, onNext, onSkip }) {
  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 4px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 4, letterSpacing: '-0.4px' }}>
          What type of venue?
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 20, lineHeight: 1.5 }}>
          Your checklists are built from this.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {VENUE_TYPES.map(v => (
            <VenueCard key={v.id} v={v} selected={selected} onSelect={onSelect} />
          ))}
        </div>
      </div>
      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <button
          onClick={onNext}
          disabled={!selected}
          style={{ ...btnPrimary, width: '100%', flex: 'none', background: selected ? BRAND : BORDER, color: selected ? '#fff' : TEXT_2, cursor: selected ? 'pointer' : 'default' }}
        >
          Next
        </button>
        <button
          onClick={onSkip}
          style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: TEXT_2, marginTop: 10, textDecoration: 'underline', padding: 4 }}
        >
          Skip — I'll set up manually
        </button>
      </div>
    </>
  );
}

// ── Step 3: How Feja works ────────────────────────────────────────────────────

const VENUE_LABELS = {
  kitchen:  'Full Kitchen',
  light:    'Light Food Service',
  catering: 'Catering & Events',
};

const FREQ_LABEL_LONG = {
  '1x': 'once a day',
  '2x': 'twice a day',
  '3x': 'three times a day',
  '4x': 'four times a day',
};

const TIME_EST = {
  '1x': '5 minutes total',
  '2x': '2 minutes each',
  '3x': '2 minutes each',
  '4x': '1–2 minutes each',
};

const SHIFT_NAMES = {
  '1x': ['Morning'],
  '2x': ['Morning', 'Afternoon'],
  '3x': ['Morning', 'Midday', 'Afternoon'],
  '4x': ['Breakfast', 'Lunch', 'Afternoon', 'Evening'],
};

const SHIFT_NAMES_CATERING = {
  '1x': ['Pre-event'],
  '2x': ['Setup', 'Service'],
  '3x': ['Setup', 'Service', 'Pack-down'],
  '4x': ['Pre-event', 'Setup', 'Service', 'Pack-down'],
};

const DAY_VIEW = {
  kitchen: {
    first: ['Fridge temps', 'Freezer temps', 'Hot holding', 'Opening checklist'],
    mid:   ['Fridge temps', 'Hot holding', 'Cooking temps'],
    last:  ['Fridge temps', 'Hot holding', 'Closing checklist'],
  },
  light: {
    first: ['Fridge temps', 'Freezer temps', 'Opening checklist'],
    mid:   ['Fridge temps'],
    last:  ['Fridge temps', 'Closing checklist'],
  },
  catering: {
    first: ['Fridge temps', 'Freezer temps', 'Hot holding', 'Transport check'],
    mid:   ['Fridge temps', 'Hot holding'],
    last:  ['Fridge temps', 'Hot holding', 'Closing checklist'],
  },
};

function buildShifts(venueType, checkFreq) {
  const day = DAY_VIEW[venueType] || DAY_VIEW.kitchen;
  const nameMap = venueType === 'catering' ? SHIFT_NAMES_CATERING : SHIFT_NAMES;
  const names = nameMap[checkFreq] || nameMap['2x'];
  if (names.length === 1) return [{ name: names[0], items: day.first }];
  if (names.length === 2) return [{ name: names[0], items: day.first }, { name: names[1], items: day.last }];
  if (names.length === 3) return [{ name: names[0], items: day.first }, { name: names[1], items: day.mid }, { name: names[2], items: day.last }];
  return [{ name: names[0], items: day.first }, { name: names[1], items: day.mid }, { name: names[2], items: day.mid.slice(0, 2) }, { name: names[3], items: day.last }];
}

function StepHowItWorks({ venueType, checkFreq, onBack, onNext }) {
  const shifts = buildShifts(venueType, checkFreq);
  const venueLabel = VENUE_LABELS[venueType] || 'your venue';
  const freqText = FREQ_LABEL_LONG[checkFreq] || 'twice a day';
  const timeEst = TIME_EST[checkFreq] || '~2 min per check';

  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 16px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 6, letterSpacing: '-0.4px' }}>
          You're set up for {venueLabel}.
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 22, lineHeight: 1.5 }}>
          {freqText.charAt(0).toUpperCase() + freqText.slice(1)}, you'll run through your checks — about {timeEst}.
        </p>

        {/* Day timeline */}
        <div style={{ position: 'relative', marginBottom: 20, padding: '0 8px' }}>
          <div style={{ position: 'absolute', top: 7, left: 8, right: 8, height: 2, background: BORDER, borderRadius: 2 }} />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
            {shifts.map((shift, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: BRAND, border: '3px solid #fff',
                  boxShadow: `0 0 0 2px ${BRAND}`, zIndex: 1,
                }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: TEXT_2, whiteSpace: 'nowrap' }}>
                  {shift.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Shift breakdown */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: shifts.length === 1 ? '1fr' : '1fr 1fr',
          gap: 8, marginBottom: 14,
        }}>
          {shifts.map((shift, i) => (
            <div key={i} style={{
              background: '#FAF7F2', borderRadius: 12,
              border: `1px solid ${BORDER}`, padding: '10px 12px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: BRAND, fontVariant: 'small-caps', letterSpacing: '0.04em', marginBottom: 7 }}>
                {shift.name}
              </div>
              {shift.items.map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 4, height: 4, borderRadius: '50%', background: TEXT_2, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: TEXT_1, lineHeight: 1.3 }}>{item}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Compliance note */}
        <div style={{
          padding: '12px 14px', borderRadius: 12,
          background: '#FAF7F2', border: `1px solid ${BORDER}`,
          fontSize: 13, color: TEXT_2, lineHeight: 1.5,
        }}>
          Everything you log counts toward your Standard 3.2.2 records. Nothing extra needed.
        </div>
      </div>

      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={btnBack}>←</button>
          <button onClick={onNext} style={btnPrimary}>Set up my equipment →</button>
        </div>
      </div>
    </>
  );
}

// ── Step 3: Temperature checks ────────────────────────────────────────────────

function StepTemps({ venueType, temps, setTemps, onBack, onNext }) {
  const types = TEMP_TYPES_BY_VENUE[venueType] || [];

  const changeCount = (type, delta) => {
    setTemps(prev => {
      const current = prev[type] || { count: 0, names: [] };
      const nextCount = Math.max(0, Math.min(8, current.count + delta));
      const names = nextCount > current.count
        ? [...current.names, getDefaultName(type, current.names.length, venueType)]
        : current.names.slice(0, nextCount);
      return { ...prev, [type]: { count: nextCount, names } };
    });
  };

  const updateName = (type, i, value) => {
    setTemps(prev => {
      const names = [...(prev[type]?.names || [])];
      names[i] = value;
      return { ...prev, [type]: { ...prev[type], names } };
    });
  };

  const hasAny = types.some(t => (temps[t]?.count || 0) > 0);

  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 4px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 4, letterSpacing: '-0.4px' }}>
          Set up your equipment
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 20, lineHeight: 1.5 }}>
          We'll create a check for each one.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {types.map(type => {
            const meta = TEMP_META[type];
            const state = temps[type] || { count: 0, names: [] };
            const isCooking = type === 'cooking';
            const active = state.count > 0;

            return (
              <div key={type}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  padding: '12px 14px', borderRadius: 12,
                  background: active ? BRAND_BG : '#FAF7F2',
                  border: `1.5px solid ${active ? BRAND : BORDER}`,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: active ? BRAND : TEXT_1 }}>{meta.label}</div>
                    <div style={{ fontSize: 12, color: TEXT_2, marginTop: 1 }}>
                      {meta.threshold.max !== undefined ? `Max ${meta.threshold.max}°C` : `Min ${meta.threshold.min}°C`}
                    </div>
                  </div>
                  {isCooking ? (
                    <button
                      onClick={() => changeCount(type, active ? -1 : 1)}
                      style={{
                        height: 32, padding: '0 16px', borderRadius: 8,
                        border: `1.5px solid ${active ? BRAND : BORDER}`,
                        background: active ? BRAND : '#fff',
                        color: active ? '#fff' : TEXT_2,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {active ? '✓ Yes' : 'Add'}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button
                        onClick={() => changeCount(type, -1)}
                        disabled={state.count === 0}
                        style={{
                          width: 32, height: 32, borderRadius: 8,
                          border: `1.5px solid ${BORDER}`,
                          background: state.count === 0 ? '#f5f5f5' : '#fff',
                          color: state.count === 0 ? '#ccc' : TEXT_1,
                          fontSize: 20, cursor: state.count === 0 ? 'default' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                        }}
                      >−</button>
                      <span style={{ fontSize: 16, fontWeight: 700, color: TEXT_1, minWidth: 14, textAlign: 'center' }}>
                        {state.count}
                      </span>
                      <button
                        onClick={() => changeCount(type, 1)}
                        disabled={state.count >= 8}
                        style={{
                          width: 32, height: 32, borderRadius: 8,
                          border: `1.5px solid ${active ? BRAND : BORDER}`,
                          background: active ? BRAND : '#fff',
                          color: active ? '#fff' : TEXT_1,
                          fontSize: 20, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                          transition: 'all 0.15s',
                        }}
                      >+</button>
                    </div>
                  )}
                </div>

                {active && !isCooking && (
                  <div style={{ paddingLeft: 14, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {state.names.map((name, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: TEXT_2, width: 16, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                        <input
                          value={name}
                          onChange={e => updateName(type, i, e.target.value)}
                          placeholder={getDefaultName(type, i, venueType)}
                          style={{
                            flex: 1, height: 36, borderRadius: 8,
                            border: `1.5px solid ${BORDER}`, padding: '0 10px',
                            fontSize: 14, color: TEXT_1, background: '#fff', outline: 'none',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={btnBack}>←</button>
          <button onClick={onNext} style={btnPrimary}>
            {hasAny ? 'Next' : 'Skip temps'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Step 4: Checklists ────────────────────────────────────────────────────────

const FREQ_CYCLE = { daily: 'weekly', weekly: 'monthly', monthly: 'daily' };
const FREQ_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const FREQ_BG    = { daily: 'rgba(37,99,235,0.1)', weekly: 'rgba(124,58,237,0.1)', monthly: 'rgba(8,145,178,0.1)' };
const FREQ_COLOR = { daily: '#2563eb', weekly: '#7c3aed', monthly: '#0891b2' };

function StepChecklists({ checklists, setChecklists, onBack, onNext }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [newItem, setNewItem] = useState('');

  const toggleEnabled = (key) => {
    setChecklists(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
    if (!checklists[key].enabled) { setExpandedKey(key); setNewItem(''); }
    else if (expandedKey === key) setExpandedKey(null);
  };

  const cycleFreq = (idx) => {
    setChecklists(prev => {
      const items = [...prev.cleaning.items];
      items[idx] = { ...items[idx], frequency: FREQ_CYCLE[items[idx].frequency] || 'daily' };
      return { ...prev, cleaning: { ...prev.cleaning, items } };
    });
  };

  const addCleaningItem = () => {
    const name = newItem.trim();
    if (!name) return;
    setChecklists(prev => ({
      ...prev,
      cleaning: { ...prev.cleaning, items: [...prev.cleaning.items, { name, frequency: 'daily' }] },
    }));
    setNewItem('');
  };

  const removeCleaningItem = (idx) => {
    setChecklists(prev => ({
      ...prev,
      cleaning: { ...prev.cleaning, items: prev.cleaning.items.filter((_, i) => i !== idx) },
    }));
  };

  const toggleItem = (key, idx) => {
    setChecklists(prev => {
      const items = [...prev[key].items];
      items[idx] = { ...items[idx], on: !items[idx].on };
      return { ...prev, [key]: { ...prev[key], items } };
    });
  };

  const addItem = (key) => {
    const name = newItem.trim();
    if (!name) return;
    setChecklists(prev => ({
      ...prev,
      [key]: { ...prev[key], items: [...prev[key].items, { name, on: true }] },
    }));
    setNewItem('');
  };

  const removeItem = (key, idx) => {
    setChecklists(prev => ({
      ...prev,
      [key]: { ...prev[key], items: prev[key].items.filter((_, i) => i !== idx) },
    }));
  };

  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 4px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 4, letterSpacing: '-0.4px' }}>
          Checklists
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 20, lineHeight: 1.5 }}>
          Select what your venue needs. Items are pre-filled — tap to expand.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.keys(CHECKLIST_LABELS).map(key => {
            const cl = checklists[key];
            const isExpanded = expandedKey === key && cl.enabled;

            return (
              <div key={key} style={{
                borderRadius: 12,
                border: `1.5px solid ${cl.enabled ? BRAND : BORDER}`,
                background: cl.enabled ? BRAND_BG : '#fff',
                overflow: 'hidden', transition: 'border-color 0.15s, background 0.15s',
              }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', cursor: 'pointer', gap: 10 }}
                  onClick={() => cl.enabled ? (setExpandedKey(isExpanded ? null : key), setNewItem('')) : toggleEnabled(key)}
                >
                  <div
                    onClick={e => { e.stopPropagation(); toggleEnabled(key); }}
                    style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${cl.enabled ? BRAND : '#ccc'}`,
                      background: cl.enabled ? BRAND : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {cl.enabled && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: cl.enabled ? BRAND : TEXT_1 }}>
                      {CHECKLIST_LABELS[key]}
                    </div>
                    <div style={{ fontSize: 12, color: TEXT_2, marginTop: 1 }}>
                      {key === 'cleaning'
                        ? `${cl.items.filter(i => i.frequency === 'daily').length} daily · ${cl.items.filter(i => i.frequency === 'weekly').length} weekly · ${cl.items.filter(i => i.frequency === 'monthly').length} monthly`
                        : (() => { const n = cl.items.filter(i => i.on !== false).length; return `${n} item${n !== 1 ? 's' : ''}`; })()}
                    </div>
                  </div>
                  {cl.enabled && (
                    <span style={{ fontSize: 18, color: TEXT_2, display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>
                  )}
                </div>

                {isExpanded && (
                  <div style={{ padding: '4px 14px 14px', borderTop: `1px solid ${BORDER}` }}>
                    {key === 'cleaning' ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_2, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '10px 0 8px' }}>
                          Tap frequency to change
                        </div>
                        {cl.items.map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ flex: 1, fontSize: 13, color: TEXT_1 }}>{item.name}</span>
                            <button
                              onClick={() => cycleFreq(idx)}
                              style={{
                                padding: '3px 8px', borderRadius: 6, border: 'none',
                                background: FREQ_BG[item.frequency],
                                color: FREQ_COLOR[item.frequency],
                                fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                              }}
                            >
                              {FREQ_LABEL[item.frequency]}
                            </button>
                            <button
                              onClick={() => removeCleaningItem(idx)}
                              style={{ background: 'none', border: 'none', color: '#bbb', fontSize: 18, cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                            >×</button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                          <input
                            value={newItem}
                            onChange={e => setNewItem(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addCleaningItem()}
                            placeholder="Add item..."
                            style={{
                              flex: 1, height: 34, borderRadius: 8,
                              border: `1.5px solid ${BORDER}`, padding: '0 10px',
                              fontSize: 13, color: TEXT_1, background: '#fff', outline: 'none',
                            }}
                          />
                          <button
                            onClick={addCleaningItem}
                            style={{
                              height: 34, padding: '0 12px', borderRadius: 8, border: 'none',
                              background: BRAND, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}
                          >Add</button>
                        </div>
                      </>
                    ) : (
                      <div style={{ paddingTop: 10 }}>
                        {cl.items.map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div
                              onClick={() => toggleItem(key, idx)}
                              style={{
                                width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                                border: `2px solid ${item.on !== false ? BRAND : '#ccc'}`,
                                background: item.on !== false ? BRAND : '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'all 0.15s',
                              }}
                            >
                              {item.on !== false && (
                                <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                                  <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <span style={{ flex: 1, fontSize: 13, color: item.on !== false ? TEXT_1 : '#aaa', textDecoration: item.on !== false ? 'none' : 'line-through' }}>{item.name}</span>
                            <button
                              onClick={() => removeItem(key, idx)}
                              style={{ background: 'none', border: 'none', color: '#bbb', fontSize: 18, cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                            >×</button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                          <input
                            value={newItem}
                            onChange={e => setNewItem(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addItem(key)}
                            placeholder="Add item..."
                            style={{
                              flex: 1, height: 34, borderRadius: 8,
                              border: `1.5px solid ${BORDER}`, padding: '0 10px',
                              fontSize: 13, color: TEXT_1, background: '#fff', outline: 'none',
                            }}
                          />
                          <button
                            onClick={() => addItem(key)}
                            style={{
                              height: 34, padding: '0 12px', borderRadius: 8, border: 'none',
                              background: BRAND, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}
                          >Add</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={btnBack}>←</button>
          <button onClick={onNext} style={btnPrimary}>Review</button>
        </div>
      </div>
    </>
  );
}

// ── Step 5: Check schedule ────────────────────────────────────────────────────

const CHECK_FREQ_OPTIONS = [
  { id: '1x', label: 'Once a day',        sub: 'One check per day',                      icon: <Sun size={26} /> },
  { id: '2x', label: 'Twice a day',       sub: 'Morning and afternoon — most common',    icon: <Sunrise size={26} /> },
  { id: '3x', label: 'Three times a day', sub: 'Morning, midday and evening',            icon: <Sunset size={26} /> },
  { id: '4x', label: 'Four times a day',  sub: 'Every 3–4 hours throughout the day',     icon: <Clock size={26} /> },
];

function StepCheckFrequency({ selected, onSelect, onBack, onNext }) {
  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 4px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 4, letterSpacing: '-0.4px' }}>
          How often do you run checks?
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 20, lineHeight: 1.5 }}>
          This sets up your shift schedule. You can change it in Settings.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CHECK_FREQ_OPTIONS.map(opt => {
            const isOn = selected === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => onSelect(opt.id)}
                style={{
                  padding: '16px', borderRadius: 14, textAlign: 'left',
                  border: `2px solid ${isOn ? BRAND : BORDER}`,
                  background: isOn ? BRAND_BG : '#fff',
                  cursor: 'pointer', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: 14,
                  position: 'relative', width: '100%',
                }}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                  background: isOn ? BRAND : '#F5F2ED',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: isOn ? '#fff' : TEXT_2, transition: 'background 0.15s, color 0.15s',
                }}>
                  {opt.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: isOn ? BRAND : TEXT_1 }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: TEXT_2, marginTop: 3, lineHeight: 1.4 }}>{opt.sub}</div>
                </div>
                {isOn && (
                  <div style={{ position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%', background: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkmark />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={btnBack}>←</button>
          <button
            onClick={onNext}
            disabled={!selected}
            style={{ ...btnPrimary, flex: 1, background: selected ? BRAND : BORDER, color: selected ? '#fff' : TEXT_2, cursor: selected ? 'pointer' : 'default' }}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

// ── Step 6: Review ────────────────────────────────────────────────────────────

function StepReview({ sections, onBack, onConfirm }) {
  const tempSections = sections.filter(s => Object.keys(s.threshold || {}).length > 0);
  const taskSections = sections.filter(s => Object.keys(s.threshold || {}).length === 0);

  return (
    <>
      <div style={{ flex: 1, padding: '8px 20px 4px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_1, marginBottom: 4, letterSpacing: '-0.4px' }}>
          Review your setup
        </h1>
        <p style={{ fontSize: 14, color: TEXT_2, marginBottom: 20, lineHeight: 1.5 }}>
          {sections.length} section{sections.length !== 1 ? 's' : ''} ready. You can adjust in Settings later.
        </p>

        {tempSections.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_2, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
              Temperature Checks · {tempSections.length}
            </div>
            {tempSections.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 10,
                background: '#FAF7F2', border: `1px solid ${BORDER}`, marginBottom: 6,
              }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: TEXT_1 }}>{s.title}</span>
                <span style={{ fontSize: 12, color: TEXT_2 }}>
                  {s.threshold.max !== undefined ? `Max ${s.threshold.max}°C` : `Min ${s.threshold.min}°C`}
                </span>
              </div>
            ))}
          </div>
        )}

        {taskSections.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_2, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
              Checklists · {taskSections.length}
            </div>
            {taskSections.map(s => (
              <div key={s.id} style={{
                padding: '10px 12px', borderRadius: 10,
                background: '#FAF7F2', border: `1px solid ${BORDER}`, marginBottom: 6,
              }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: TEXT_1 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: TEXT_2, marginTop: 2 }}>
                  {s.items.length} item{s.items.length !== 1 ? 's' : ''}
                  {s.itemFrequencies && (
                    <span> · {Object.values(s.itemFrequencies).filter(f => f === 'daily').length} daily, {Object.values(s.itemFrequencies).filter(f => f === 'weekly').length} weekly, {Object.values(s.itemFrequencies).filter(f => f === 'monthly').length} monthly</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {sections.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
            <p style={{ fontSize: 14, color: '#ccc', textAlign: 'center' }}>Nothing added yet — go back and add some checks.</p>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={btnBack}>←</button>
          <button
            onClick={onConfirm}
            disabled={sections.length === 0}
            style={{ ...btnPrimary, background: sections.length > 0 ? BRAND : BORDER, color: sections.length > 0 ? '#fff' : TEXT_2, cursor: sections.length > 0 ? 'pointer' : 'default' }}
          >
            Start logging →
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function OnboardingModal({ onComplete }) {
  const [step, setStep] = useState(1);
  const [venueType, setVenueType] = useState(null);
  const [temps, setTemps] = useState({});
  const [checklists, setChecklists] = useState({});
  const [checkFreq, setCheckFreq] = useState('2x');

  const handleSelectVenueType = (type) => {
    setVenueType(type);
    setTemps(initTemps(type));
    setChecklists(initChecklists(type));
  };

  const builtSections = React.useMemo(
    () => (venueType ? buildSections(venueType, temps, checklists) : []),
    [venueType, temps, checklists]
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, background: 'rgba(26,26,26,0.55)',
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: '#fff', borderRadius: 24,
        border: `1px solid ${BORDER}`, boxShadow: '0 20px 60px rgba(26,26,26,0.18)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '90vh', overflow: 'hidden',
      }}>
        <ProgressDots step={step} total={6} />

        {step === 1 && (
          <StepVenueType
            selected={venueType}
            onSelect={handleSelectVenueType}
            onNext={() => setStep(2)}
            onSkip={() => onComplete([], null, checkFreq)}
          />
        )}
        {step === 2 && (
          <StepCheckFrequency
            selected={checkFreq}
            onSelect={setCheckFreq}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <StepHowItWorks
            venueType={venueType}
            checkFreq={checkFreq}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <StepTemps
            venueType={venueType}
            temps={temps}
            setTemps={setTemps}
            onBack={() => setStep(3)}
            onNext={() => setStep(5)}
          />
        )}
        {step === 5 && (
          <StepChecklists
            checklists={checklists}
            setChecklists={setChecklists}
            onBack={() => setStep(4)}
            onNext={() => setStep(6)}
          />
        )}
        {step === 6 && (
          <StepReview
            sections={builtSections}
            onBack={() => setStep(5)}
            onConfirm={() => onComplete(builtSections, venueType, checkFreq)}
          />
        )}
      </div>
    </div>
  );
}
